import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import assistantApi from "@/api/assistant";
import documentTypeApi from "@/api/documentType";
import notificationApi from "@/api/notification";
import resumeApi from "@/api/resume";
import sectionApi from "@/api/section";
import templateApi from "@/api/template";
import userApi from "@/api/user";
import userJobApi from "@/api/userJob";
import { useAppSelector } from "@/hooks/types";
import { setAssistantItems } from "@/store/assistant/slice";
import { setDocumentTypes } from "@/store/documentType/slice";
import { setResumes, setExamples, setActiveResume } from "@/store/resume/slice";
import { setSections } from "@/store/sections/slice";
import { store } from "@/store/store";
import { setTemplates } from "@/store/template/slice";
import { getIsAdminUser, getUserDetails } from "@/store/user/selectors";
import { setUserDetails, setUserFeatures } from "@/store/user/slice";
import { setUserJobs } from "@/store/userJob/slice";
import { setWizardUserPreferences } from "@/store/wizard/slice";
import { getIsDevelopmentEnv } from "@/utils/env";
import { getIsResumePage } from "@/utils/routes";
import { trackClarityIdentify } from "@/utils/tracking";

// Define query keys
export const queryKeys = {
    activeResume: (id: string) => ["activeResume", id],
    resumes: ["resumes"],
    sections: ["sections"],
    templates: ["templates"],
    documentTypes: ["documentTypes"],
    examples: ["examples"],
    assistantItems: ["assistantItems"],
    creditActions: ["creditActions"],
    userJobs: ["userJobs"],
    tenant: ["tenant"],
    pendingNotifications: ["pendingNotifications"],
    userFeatures: ["userFeatures"],
} as const;

const ONE_HOUR = 30 * 60 * 1000;
const FIFTEEN_MINUTES = 15 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;

export const useAppData = (
    resumeId?: string,
    includeAssistantItems = false,
) => {
    const dispatch = useDispatch();
    const isAdmin = useAppSelector(getIsAdminUser);
    const isDevelopment = getIsDevelopmentEnv();
    const userDetails = useAppSelector(getUserDetails);

    const userDataQuery = useQuery({
        queryKey: ["userData"],
        queryFn: async () => {
            const accessToken = window.localStorage.getItem("accessToken");
            const parsedAccessToken = accessToken
                ? JSON.parse(accessToken)
                : {};

            if (
                parsedAccessToken?.token &&
                parsedAccessToken?.token !== "null"
            ) {
                // If we already have admin status, don't refetch on hot reload
                if (isAdmin && isDevelopment) {
                    return userDetails;
                }

                const response = await userApi.getMeApi();
                store.dispatch(setUserDetails(response));
                store.dispatch(
                    setWizardUserPreferences(response.preferences || null),
                );
                trackClarityIdentify(response.email);
                return response;
            }
            return null;
        },
        retry: false,
        staleTime: FIFTEEN_MINUTES,
        gcTime: THIRTY_MINUTES,
        refetchOnMount: isAdmin ? false : "always", // Don't refetch for admins on mount
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
    });

    const userFeaturesQuery = useQuery({
        queryKey: ["userFeatures"],
        queryFn: () => userApi.getFeaturesApi(),
        staleTime: FIFTEEN_MINUTES,
        gcTime: THIRTY_MINUTES,
        refetchOnMount: "always",
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
    });

    const activeResumeQuery = useQuery({
        queryKey: queryKeys.activeResume(resumeId!),
        queryFn: () => resumeApi.getResume(resumeId!),
        enabled: !!resumeId,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: "always",
    });

    const resumesQuery = useQuery({
        queryKey: queryKeys.resumes,
        queryFn: () => resumeApi.getAllResume(false),
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: isDevelopment ? false : "always",
        // refetchOnWindowFocus: "always",
        refetchOnReconnect: isDevelopment ? false : "always",
        // refetchInterval: 0,
    });

    const assistantItemsQuery = useQuery({
        queryKey: queryKeys.assistantItems,
        queryFn: () => assistantApi.getUserAssistantItems(),
        enabled: includeAssistantItems,
        staleTime: ONE_HOUR,
    });

    const sectionsQuery = useQuery({
        queryKey: queryKeys.sections,
        queryFn: () => sectionApi.getAllSections(),
        staleTime: ONE_HOUR,
        gcTime: ONE_HOUR,
    });

    const templatesQuery = useQuery({
        queryKey: queryKeys.templates,
        queryFn: () => templateApi.getAllTemplates(),
        staleTime: ONE_HOUR,
        gcTime: ONE_HOUR,
    });

    const documentTypesQuery = useQuery({
        queryKey: queryKeys.documentTypes,
        queryFn: () => documentTypeApi.getAllDocumentTypes(),
        staleTime: ONE_HOUR,
        gcTime: ONE_HOUR,
    });

    const examplesQuery = useQuery({
        queryKey: queryKeys.examples,
        queryFn: () => resumeApi.getExamples(),
        staleTime: ONE_HOUR,
        gcTime: ONE_HOUR,
    });

    const userJobsQuery = useQuery({
        queryKey: queryKeys.userJobs,
        queryFn: () => userJobApi.getAllUserJobs(),
        staleTime: ONE_HOUR,
        gcTime: ONE_HOUR,
    });

    // Add notifications query with polling
    const pendingNotificationsQuery = useQuery({
        queryKey: queryKeys.pendingNotifications,
        queryFn: () => notificationApi.getPendingNotifications(),
        enabled: !!userDetails?.id,
        refetchInterval: FIVE_MINUTES,
        refetchIntervalInBackground: true,
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });

    useEffect(() => {
        if (userFeaturesQuery.data) {
            dispatch(setUserFeatures(userFeaturesQuery.data));
        }
    }, [userFeaturesQuery.data, dispatch]);

    useEffect(() => {
        if (userJobsQuery.data) {
            const userJobs = [...(userJobsQuery.data.results ?? [])];
            dispatch(setUserJobs(userJobs));
        }
    }, [userJobsQuery.data, dispatch]);

    useEffect(() => {
        if (activeResumeQuery.data) {
            dispatch(setActiveResume(activeResumeQuery.data));
        }
    }, [activeResumeQuery.data, dispatch]);

    useEffect(() => {
        if (assistantItemsQuery.data) {
            dispatch(setAssistantItems(assistantItemsQuery.data.results));
        }
    }, [assistantItemsQuery.data, dispatch]);

    // Handle Redux state updates in an effect
    useEffect(() => {
        if (resumesQuery.data) {
            dispatch(setResumes(resumesQuery.data.results));
        }
    }, [resumesQuery.data, dispatch]);

    useEffect(() => {
        if (templatesQuery.data) {
            dispatch(setTemplates(templatesQuery.data.results));
        }
    }, [templatesQuery.data, dispatch]);

    useEffect(() => {
        if (documentTypesQuery.data) {
            dispatch(setDocumentTypes(documentTypesQuery.data.results));
        }
    }, [documentTypesQuery.data, dispatch]);

    useEffect(() => {
        if (examplesQuery.data) {
            dispatch(setExamples(examplesQuery.data.results));
        }
    }, [examplesQuery.data, dispatch]);

    useEffect(() => {
        if (sectionsQuery.data) {
            dispatch(setSections(sectionsQuery.data.results));
        }
    }, [sectionsQuery.data, dispatch]);

    return {
        isLoading:
            resumesQuery.isLoading ||
            sectionsQuery.isLoading ||
            templatesQuery.isLoading ||
            documentTypesQuery.isLoading ||
            examplesQuery.isLoading ||
            (includeAssistantItems && assistantItemsQuery.isLoading) ||
            (resumeId && activeResumeQuery.isLoading && !getIsResumePage()) ||
            userDataQuery.isLoading ||
            userFeaturesQuery.isLoading ||
            userJobsQuery.isLoading ||
            pendingNotificationsQuery.isLoading,
        isError:
            resumesQuery.isError ||
            sectionsQuery.isError ||
            templatesQuery.isError ||
            documentTypesQuery.isError ||
            examplesQuery.isError ||
            (includeAssistantItems && assistantItemsQuery.isError) ||
            (resumeId && activeResumeQuery.isError) ||
            userDataQuery.isError ||
            userFeaturesQuery.isError ||
            userJobsQuery.isError ||
            pendingNotificationsQuery.isError,
        error:
            resumesQuery.error ||
            sectionsQuery.error ||
            templatesQuery.error ||
            documentTypesQuery.error ||
            examplesQuery.error ||
            assistantItemsQuery.error ||
            activeResumeQuery.error ||
            userDataQuery.error ||
            userFeaturesQuery.error ||
            userJobsQuery.error ||
            pendingNotificationsQuery.error,
        pendingNotifications: pendingNotificationsQuery.data,
    };
};

export const useInvalidateResumesCache = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: queryKeys.resumes });
};

export const useInvalidateActiveResumeCache = () => {
    const queryClient = useQueryClient();
    return (resumeId: string) =>
        queryClient.invalidateQueries({
            queryKey: queryKeys.activeResume(resumeId),
        });
};

export const useInvalidateUserJobsCache = () => {
    const queryClient = useQueryClient();
    return () =>
        queryClient.invalidateQueries({ queryKey: queryKeys.userJobs });
};

export const useInvalidatePendingNotificationsCache = () => {
    const queryClient = useQueryClient();
    return () =>
        queryClient.invalidateQueries({
            queryKey: queryKeys.pendingNotifications,
        });
};

export const useInvalidateUserFeaturesCache = () => {
    const queryClient = useQueryClient();
    return () =>
        queryClient.invalidateQueries({
            queryKey: queryKeys.userFeatures,
        });
};
